/* eslint-disable react/destructuring-assignment */
import React from 'react';

import type { FC, ReactNode } from 'react';
import { Box, Button, Card, Divider, Stack, Typography } from '@packages/shared';
import { useConfig } from '@packages/utilities';
import { useIntl } from 'react-intl';
import { Installments32 } from '@packages/themes/icons';
import { ServiceLayer } from '@packages/cms-components';
import { PromotionComponentLayout3Messages } from './messages';
import { TextWithDialog } from '../../../TextWithDialog';
import { usePriceFormater } from '../../../../hooks/usePriceFormater';
import { useInstallmentCalculatorTracking } from '../../../Tracking/hooks/useInstallmentCalculatorTracking';

export type Layout3Props = {
  /**
   * the single installment value from the calculation result as float (e.g. 13.37)
   */
  singleInstallment: number;
  /**
   * the currency string from the calculation result
   */
  currency: string;
  /**
   * open dialog handler
   */
  openInstallmentCalulatorHandler: () => void;
};

/**
 * Promotion component Layout 3
 *
 * component to promote the installment calculator in layout version 3
 *
 * */
export const Layout3: FC<Layout3Props> = ({
  singleInstallment,
  currency,
  openInstallmentCalulatorHandler,
}) => {
  const { formatMessage } = useIntl();
  const formatPrice = usePriceFormater('INSTALLMENTCALCULATOR');
  const {
    installmentCalculator: { hasSpecialCreditColors },
  } = useConfig();

  const { trackGlycerinLinkClick } = useInstallmentCalculatorTracking();

  return (
    /* bgColor is a special one for credit */
    /* TODO - UX has to decide if they implement this special color into design system -> https://empiriecom.atlassian.net/browse/INFORM-2597 */
    <Card sx={{ p: 1, bgcolor: hasSpecialCreditColors ? 'rgb(238, 243, 247)' : 'grey.light' }}>
      <Stack>
        <Stack flexWrap="nowrap" direction="row" gap={1.5}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '3rem',
              height: '3rem',
              bgcolor: 'additional.main',
            }}
          >
            <Installments32 sx={{ color: 'common.white' }} width="2rem" height="2rem" />
          </Box>
          <Box>
            <Typography variant="h5" component="p" lineHeight="unset">
              {formatMessage(PromotionComponentLayout3Messages.rateText, {
                value: formatPrice(singleInstallment, currency),
              })}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              sx={{
                mt: 1,
                borderColor: 'text.darkTransparent',
              }}
              onClick={() => {
                openInstallmentCalulatorHandler();
                trackGlycerinLinkClick();
              }}
            >
              {formatMessage(PromotionComponentLayout3Messages.buttonText)}
            </Button>
          </Box>
        </Stack>
        <Divider flexItem sx={{ mt: 2, mb: 1.5 }} />
        <Typography variant="h5">
          {formatMessage(PromotionComponentLayout3Messages.legalText, {
            button: (msg: ReactNode) => (
              <TextWithDialog
                message={msg}
                dialogTitle={formatMessage(PromotionComponentLayout3Messages.modalHeadline)}
                dialogContent={<ServiceLayer serviceLayerId="installment_ratenelement" />}
                variant="h5"
                displayTrackingProps={{ category: 'dv-service-layer-layout-3' }}
              />
            ),
          })}
        </Typography>
      </Stack>
    </Card>
  );
};
